import { useTranslations } from 'next-intl';
import { useRouter } from 'next/router';
import { observer, inject } from 'mobx-react';
import CloseOneIcon from '../svg/CloseOneIcon';
import ProductImage from '../products/ProductImage';
import ButtonWithLoading from '../form-fields/ButtonWithLoading';
import { formatPrice } from '@/utils/misc';

const CartSyncModal = inject('wooCommerceStore', 'studioStore', 'generalSettingsStore')(observer(({ wooCommerceStore, studioStore, generalSettingsStore }) => {
  const t = useTranslations('Cart');
  const router = useRouter();

  if (!wooCommerceStore.isCartSyncModalOpen || !wooCommerceStore.cartSyncData) {
    return null;
  }

  // Check if cart sync modal should be shown based on settings
  if (!generalSettingsStore.showCartSyncModal) {
    return null;
  }

  const { invalidItems, outOfStockItems, validItems, priceChanges, originalItems } = wooCommerceStore.cartSyncData;
  const container = studioStore.settings.product_popup?.container;
  const productInfoBackgroundColor = studioStore.settings.product_popup?.product_info_background_color;
  const primaryColor = studioStore.settings.colors.primary_color;

  // Get the settings for showing specific sections
  const showRemovedProducts = generalSettingsStore.showCartSyncRemovedProducts;
  const showPriceChanges = generalSettingsStore.showCartSyncPriceChanges;

  let classes = "fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-2xl md:mx-auto max-h-[90vh] z-50 overscroll-contain";
  const style = {
    backgroundColor: productInfoBackgroundColor,
  };

  if (container === "stamp") {
    classes = classes + " stamp-border";
    style.backgroundImage = `radial-gradient(8px, transparent 98%, ${productInfoBackgroundColor}),linear-gradient(${productInfoBackgroundColor} 0px, ${productInfoBackgroundColor} 0px)`;
    style.backgroundColor = '';
  } else {
    classes = classes + " rounded-lg shadow-lg p-4 bg-white";
  }

  const handleContinueShopping = () => {
    wooCommerceStore.toggleCartSyncModal();
    router.push('/');
  };

  // Check if there's anything to show based on settings
  const hasRemovedProductsToShow = showRemovedProducts && invalidItems.length > 0;
  const hasPriceChangesToShow = showPriceChanges && priceChanges && validItems.some(item => {
    const originalItem = originalItems.find(i => i.id === item.id);
    return originalItem && originalItem.price !== item.price;
  });

  const hasOutOfStockProductsToShow = showRemovedProducts && outOfStockItems.length > 0;

  // If nothing to show based on settings, close the modal
  if (!hasRemovedProductsToShow && !hasPriceChangesToShow && !hasOutOfStockProductsToShow) {
    wooCommerceStore.toggleCartSyncModal();
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" data-testid="cart-sync-modal">
      <div className="fixed inset-0 transition-opacity" aria-hidden="true">
        <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
      </div>

      <div className={classes} style={style}>
        <button
          className="absolute top-4 md:-top-8 left-4 md:-left-10 rtl:right-4 rtl:md:-right-10 rtl:left-auto z-10"
          onClick={() => wooCommerceStore.toggleCartSyncModal()}
        >
          <CloseOneIcon size="28px" stroke={primaryColor} />
        </button>

        <div className="max-h-[86vh] overflow-y-auto">
          <div className="p-3 md:p-6">
            <div className="px-4 md:px-12">
              <div className="text-center">
                <h2 className="mt-3 mb-6 text-2xl font-semibold text-primary-studio">
                  {t('cart_changes_detected')}
                </h2>

                {hasRemovedProductsToShow && (
                  <div className="mb-6">
                    <h3 className="text-xl font-semibold mb-4 text-red-600">
                      {t('unavailable_products')}
                    </h3>
                    <div className="space-y-4">
                      {invalidItems.map((item) => (
                        <div key={item.id} className="flex items-center space-x-4 rtl:space-x-reverse">
                          <div className="w-16 h-16 relative">
                            <ProductImage
                              img={item.type === 'variation' ? (item?.image?.src) ? item.image : (item?.parent_images?.[0] ?? {}) : (item?.images?.[0]?.src) ? item.images[0] : {}}
                              productName={item.type === 'variation' ? item.parent_name : item.name}
                              width={64}
                              height={64}
                            />
                          </div>
                          <div className="flex-1 text-start ms-2">
                            <p className="font-semibold">{item.type === 'variation' ? item.parent_name : item.name}</p>
                            <p className="text-sm text-gray-600">{t('product_no_longer_available')}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {hasOutOfStockProductsToShow && (
                  <div className="mb-6">
                    <h3 className="text-xl font-semibold mb-4 text-red-600">
                      {t('out_of_stock_products')}
                    </h3>
                    <div className="space-y-4">
                      {outOfStockItems.map((item) => (
                        <div key={item.id} className="flex items-center space-x-4 rtl:space-x-reverse">
                          <div className="w-16 h-16 relative">
                            <ProductImage
                              img={item.type === 'variation' ? (item?.image?.src) ? item.image : (item?.parent_images?.[0] ?? {}) : (item?.images?.[0]?.src) ? item.images[0] : {}}
                              productName={item.type === 'variation' ? item.parent_name : item.name}
                              width={64}
                              height={64}
                            />
                          </div>
                          <div className="flex-1 text-start ms-2">
                            <p className="font-semibold">{item.type === 'variation' ? item.parent_name : item.name}</p>
                            <p className="text-sm text-gray-600">{t('product_out_of_stock')}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {hasPriceChangesToShow && (
                  <div className="mb-6">
                    <h3 className="text-xl font-semibold mb-4 text-yellow-600">
                      {t('price_changes')}
                    </h3>
                    <div className="space-y-4">
                      {validItems.filter(item => {
                        const originalItem = originalItems.find(i => i.id === item.id);
                        return originalItem && originalItem.price !== item.price;
                      }).map((item) => {
                        const originalItem = originalItems.find(i => i.id === item.id);
                        return (
                          <div key={item.id} className="flex items-center space-x-4 rtl:space-x-reverse">
                            <div className="w-16 h-16 relative">
                              <ProductImage
                                img={item.type === 'variation' ? (item?.image?.src) ? item.image : (item?.parent_images?.[0] ?? {}) : (item?.images?.[0]?.src) ? item.images[0] : {}}
                                productName={item.type === 'variation' ? item.parent_name : item.name}
                                width={64}
                                height={64}
                              />
                            </div>
                            <div className="flex-1 text-start ms-2">
                              <p className="font-semibold">{item.type === 'variation' ? item.parent_name : item.name}</p>
                              <p className="text-sm">
                                <span className="line-through text-gray-500">
                                  {formatPrice(originalItem.price, wooCommerceStore)}
                                </span>
                                <span className="mx-2">→</span>
                                <span className="text-yellow-600 font-semibold">
                                  {formatPrice(item.price, wooCommerceStore)}
                                </span>
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="flex justify-center gap-4">
                  <ButtonWithLoading
                    type="button"
                    onClick={handleContinueShopping}
                    text={t('back_to_home')}
                    loading={false}
                  />
                  <ButtonWithLoading
                    type="button"
                    onClick={() => wooCommerceStore.toggleCartSyncModal()}
                    text={t('continue_shopping')}
                    outline
                    loading={false}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}));

export default CartSyncModal;